import { saveShareCard } from '../../utils/shareCard';
import React, { useState } from 'react';
import { BUSINESSES } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
  fetchRepliesPage, fetchLikedIds, createReply, toggleLike, deleteContent, setDemoLikes, friendlyCallError, timeAgo,
} from '../../utils/reviews';
import {
  Avatar, AuthorBadges, Stars, LikeButton, ReportButton, DeleteButton, DemoLikesButton,
} from './bits';

// One review / comment / prompt, with its replies one level deep.
//
// Built to be screenshotted: the text is large and the author line is short,
// and a small wordmark in the corner means a cropped screenshot posted to
// TikTok or Instagram still says where it came from.
//
// DEMO TOOLS: `asDemoUser` sends replies as a generated user, and
// `onSetLikes` (admin only) sets the like count by hand.
export default function PostCard({ post, restaurant, liked, onToggleLike, onDeleted, onSetLikes, asDemoUser }) {
  const { currentUid, ensureGuest, refreshGuestProfile, user, isAdmin } = useAuth();
  const [showReplies, setShowReplies] = useState(false);
  const [replyCursor, setReplyCursor] = useState(null);
  const [repliesDone, setRepliesDone] = useState(true);
  const [replies, setReplies] = useState(null);
  const [likedReplies, setLikedReplies] = useState(new Set());
  const [replyCount, setReplyCount] = useState(post.replyCount || 0);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyBusy, setReplyBusy] = useState(false);
  const [replyError, setReplyError] = useState('');
  const [shareError, setShareError] = useState('');
  const [shareBusy, setShareBusy] = useState(false);

  // Your own, or anything at all for an admin.
  const canDelete = (item) => isAdmin || (currentUid && item.authorId === currentUid);
  const held = post.status === 'held';
  const isPrompt = post.type === 'prompt';

  async function openReplies(more = false) {
    setShowReplies(true);
    if (replies && !more) return;
    setLoadingReplies(true);
    try {
      const page = await fetchRepliesPage(restaurant.id, post.id, more ? replyCursor : null);
      const list = page.replies; setReplyCursor(page.cursor); setRepliesDone(page.done);
      setReplies(prev => more ? [...(prev || []), ...list.filter(r => !prev?.some(existing => existing.id === r.id))] : list);
      const paths = Object.fromEntries(list.map((r) => [r.id, [BUSINESSES, restaurant.id, 'posts', post.id, 'replies', r.id]]));
      const likes = await fetchLikedIds(paths, currentUid);
      setLikedReplies(prev => new Set(more ? [...prev, ...likes] : likes));
    } catch {
      setReplies([]);
    } finally {
      setLoadingReplies(false);
    }
  }

  async function sendReply(e) {
    e.preventDefault();
    if (replyBusy || replyText.trim().length < 2) return;
    setReplyBusy(true);
    setReplyError('');
    const asGuest = !user;
    try {
      await ensureGuest();
      const res = await createReply({
        businessId: restaurant.id,
        postId: post.id,
        text: replyText,
        asDemoUser: !!asDemoUser,
      });
      const reply = res.reply;
      if (reply) setReplies((prev) => [...(prev || []), reply]);
      if (res.status === 'visible') setReplyCount((n) => n + 1);
      setReplyText('');
      if (asGuest) refreshGuestProfile();
    } catch (err) {
      setReplyError(friendlyCallError(err));
    } finally {
      setReplyBusy(false);
    }
  }

  async function likeReply(reply) {
    const was = likedReplies.has(reply.id);
    const flip = (on, delta) => {
      setLikedReplies((prev) => { const next = new Set(prev); if (on) next.add(reply.id); else next.delete(reply.id); return next; });
      setReplies((prev) => prev.map((r) => (r.id === reply.id ? { ...r, likeCount: Math.max(0, (r.likeCount || 0) + delta) } : r)));
    };
    flip(!was, was ? -1 : 1);
    try {
      const asGuest = !user;
      await ensureGuest();
      await toggleLike({ businessId: restaurant.id, postId: post.id, replyId: reply.id });
      if (asGuest) refreshGuestProfile();
    } catch {
      flip(was, was ? 1 : -1);
    }
  }

  async function setReplyLikes(reply, n) {
    const res = await setDemoLikes({ businessId: restaurant.id, postId: post.id, replyId: reply.id, likeCount: n });
    setReplies((prev) => prev.map((r) => (r.id === reply.id ? { ...r, likeCount: res.likeCount } : r)));
  }

  async function deletePost() {
    await deleteContent({ businessId: restaurant.id, postId: post.id });
    onDeleted?.(post);
  }

  async function deleteReply(reply) {
    await deleteContent({ businessId: restaurant.id, postId: post.id, replyId: reply.id });
    setReplies((prev) => prev.filter((r) => r.id !== reply.id));
    if (reply.status === 'visible') setReplyCount((n) => Math.max(0, n - 1));
  }

  return (
    <article className={`post${isPrompt ? ' post--prompt' : ''}${post.isRestaurantResponse ? ' post--restaurant' : ''}${held ? ' post--held' : ''}`}>
      {isPrompt && <p className="post__pin">📌 Pinned</p>}
      <header className="post__head">
        <Avatar name={post.authorHandle} restaurant={post.isRestaurantResponse} />
        <div className="post__who">
          <span className="post__name">{post.authorHandle}</span>
          <AuthorBadges item={post} />
          <span className="post__time">· {timeAgo(post.createdAt)}</span>
        </div>
      </header>

      {post.rating != null && <Stars value={post.rating} />}
      <p className="post__text">{post.text}</p>
      {shareError && <p className="composer__error" role="alert">{shareError}</p>}

      {held && (
        <p className="post__held-note">
          Only you can see this for now — it's being reviewed before it appears publicly.
        </p>
      )}
      {held && canDelete(post) && (
        <footer className="post__actions">
          <DeleteButton onDelete={deletePost} what={post.type === 'review' ? 'review' : 'comment'} />
        </footer>
      )}

      {!held && (
        <footer className="post__actions">
          <LikeButton liked={liked} count={post.likeCount} onClick={() => onToggleLike(post)} />
          <button type="button" className="post-action" onClick={() => (showReplies ? setShowReplies(false) : openReplies())}>
            {showReplies ? 'Hide replies' : replyCount > 0 ? `${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}` : isPrompt ? 'Answer' : 'Reply'}
          </button>
          {onSetLikes && <DemoLikesButton count={post.likeCount} onSet={(n) => onSetLikes(post, n)} />}
          {canDelete(post) ? (
            <DeleteButton onDelete={deletePost} what={isPrompt ? 'prompt' : post.type === 'review' ? 'review' : 'comment'} />
          ) : (
            <ReportButton businessId={restaurant.id} postId={post.id} ensureGuest={ensureGuest} />
          )}
          <button type="button" className="post-action" disabled={shareBusy} onClick={async () => { setShareBusy(true); setShareError(''); try { await saveShareCard(restaurant, post); } catch (e) { setShareError(e.message); } finally { setShareBusy(false); } }}>{shareBusy ? 'Creating image…' : 'Save image'}</button>
          <img src="/assets/logo.png" alt="" className="post__wordmark" aria-hidden="true" />
        </footer>
      )}

      {showReplies && (
        <div className="post__replies">
          {!repliesDone && <button type="button" className="post-action" disabled={loadingReplies} onClick={() => openReplies(true)}>Load more replies</button>}
          {loadingReplies && <div className="post__replies-loading">Loading replies…</div>}
          {replies?.map((r) => (
            <div key={r.id} className={`reply${r.isRestaurantResponse ? ' reply--restaurant' : ''}`}>
              <Avatar name={r.authorHandle} restaurant={r.isRestaurantResponse} />
              <div className="reply__body">
                <div className="post__who">
                  <span className="post__name">{r.authorHandle}</span>
                  <AuthorBadges item={r} />
                  <span className="post__time">· {timeAgo(r.createdAt)}</span>
                </div>
                <p className="reply__text">{r.text}</p>
                {r.status === 'held' && (
                  <p className="post__held-note">Only you can see this for now — it's being reviewed.</p>
                )}
                <div className="post__actions post__actions--reply">
                  {r.status !== 'held' && (
                    <LikeButton liked={likedReplies.has(r.id)} count={r.likeCount} onClick={() => likeReply(r)} />
                  )}
                  {isAdmin && r.status !== 'held' && (
                    <DemoLikesButton count={r.likeCount} onSet={(n) => setReplyLikes(r, n)} />
                  )}
                  {canDelete(r) ? (
                    <DeleteButton onDelete={() => deleteReply(r)} what="reply" />
                  ) : r.status !== 'held' && (
                    <ReportButton businessId={restaurant.id} postId={post.id} replyId={r.id} ensureGuest={ensureGuest} />
                  )}
                </div>
              </div>
            </div>
          ))}
          <form className="reply-form" onSubmit={sendReply}>
            <input
              className="reply-form__input"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value.slice(0, 500))}
              placeholder={isPrompt ? 'Your answer…' : `Reply to ${post.authorHandle}…`}
              aria-label="Write a reply"
              disabled={replyBusy}
            />
            <button type="submit" className="reply-form__send" disabled={replyBusy || replyText.trim().length < 2}>
              {replyBusy ? '…' : 'Reply'}
            </button>
          </form>
          {replyError && <p className="composer__error" role="alert">{replyError}</p>}
        </div>
      )}
    </article>
  );
}
